import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Tooltip } from '@strivelabs/ui-core/v2';
import { useSidebar } from '@strivelabs/ui-blocks/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { useMeteringSummary } from '@/hooks/useMetering';
import { WARN_RATIO, isUnlimitedPlan } from '@/pages/settings/billing/format';

/** Where the warning sends someone who wants to do something about it. */
const BILLING_USAGE_ROUTE = '/ui/settings/billing/usage';

/** Where a workspace with no plan goes to pick one. */
const BILLING_PLAN_ROUTE = '/ui/settings/billing/plan';

/**
 * How much of the allowance has gone.
 *
 * Both figures come from the same reading, so their sum is the allowance as the
 * vendor saw it at that moment — which is truer than the plan's nominal
 * allowance, since a top-up raises what is actually available mid-period.
 *
 * @param used - Credits spent in the open period.
 * @param left - Credits still available.
 *
 * @returns The share spent, between 0 and 1, or null when there is no allowance
 * to take a share of.
 */
function usedRatio(used: number, left: number): number | null {
  const allowance = used + left;

  if (!Number.isFinite(allowance) || allowance <= 0) return null;

  return used / allowance;
}

/**
 * Sidebar warning that this workspace is running out of credits.
 *
 * Sits above the identity row rather than on the billing page, because the
 * person who needs to see it is mid-task somewhere else entirely — a warning
 * only visible in Billing is one nobody reads until they have already been cut
 * off.
 *
 * Renders nothing below the threshold. An always-present meter would be ignored
 * by the time it mattered, and it would put a number nobody asked for beside
 * every screen in the product.
 */
export function CreditWarning() {
  const { collapsed } = useSidebar();
  const summary = useMeteringSummary();

  const metering = summary.data;

  // Nothing is claimed while the read is in flight, and nothing is claimed when
  // metering is off — a workspace that is not metered cannot run out.
  if (!metering?.enabled) return null;

  if (isUnlimitedPlan(metering.plan)) return null;

  const noPlan = Boolean(metering.wallet) && !metering.plan;
  const ratio = usedRatio(metering.credits_used, metering.credits_left);

  const hasThreshold = metering.low_credit_threshold !== null;
  const exhausted = noPlan || metering.credits_left <= 0;
  const warn = hasThreshold ? metering.credits_low : ratio !== null && ratio >= WARN_RATIO;

  if (!exhausted && !warn) return null;

  const percent = Math.min(100, Math.round((ratio ?? 1) * 100));
  const route = noPlan ? BILLING_PLAN_ROUTE : BILLING_USAGE_ROUTE;
  let message = hasThreshold
    ? 'Credits running low. Add credits to keep working.'
    : `${percent}% of this period's credits used.`;
  if (noPlan) {
    message = 'No plan. Choose a plan to keep working.';
  } else if (exhausted) {
    message = 'Credits used up. Work will not start until you top up.';
  }

  // The fill tokens are unreadable as text, so the text takes the -text pair.
  // This is the same pairing the v2 badge uses for its warning and danger
  // states, which is what these two are.
  return (
    <Tooltip label={message} side="right" disabled={!collapsed}>
      <Link
        to={route}
        aria-label={message}
        className={cn(
          'mx-2 mb-1 flex min-w-0 items-start gap-2 rounded-v2-md border px-2 py-1.5',
          'text-v2-xs transition-colors',
          exhausted
            ? 'border-v2-destructive/30 bg-v2-destructive/10 text-v2-destructive-text hover:bg-v2-destructive/15'
            : 'border-v2-warning/30 bg-v2-warning/10 text-v2-warning-text hover:bg-v2-warning/15',
          collapsed && 'justify-center px-0',
        )}
      >
        <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
        {!collapsed && <span className="min-w-0 flex-1 break-words leading-snug">{message}</span>}
      </Link>
    </Tooltip>
  );
}
